import { LocationVarKey } from "../Variables/Generated/LocationVarKey.Data";
import { LocationUrlEntry, LocationUrlName } from "./LocationUrl.Interface";

/**
 * The one place a link is declared (ENG-3138; guide: `docs/snowy/guides/location-urls.md`).
 *
 * Lives in `types` rather than beside the resolver so the studio can offer these links as
 * `{{Type:Key}}` variables by their labels; resolving one is server-side only.
 *
 * Each entry lists its sources in precedence order. That precedence used to be a per-caller
 * decision — `lookup_info` read the Cortex pair, a Conversation Starter read the plain key, and
 * nothing at all could produce a spot.is page — which is how a shipped starter came to reference
 * `{{Location:SpotReviewURL}}`, a variable that never existed.
 *
 * Every source reads a `Cortex*` variable — Snowy reads no legacy location variables, ever
 * (standing rule; the legacy fallbacks this registry originally carried were a violation, removed
 * 2026-08-28). The variable ids are `Location:CortexLink*` so a link can never be confused with a
 * stored variable of the same name.
 *
 * To add a link: add an entry here. It is then available to `lookup_info`, to message text as a
 * `{{Type:Key}}` variable, and to any Snowy code — no caller changes.
 */
export const LOCATION_URL_REGISTRY: Record<LocationUrlName, LocationUrlEntry> = {
  review: {
    name: "review",
    label: "Review Link",
    // Named `review`, not `google_review`: orgs point this at whatever review platform they use
    // (KO Storage's `GoogleReviewURL` values are Birdeye links). Chuck, 2026-08-27.
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLGoogleReviewEnabled, valueKey: LocationVarKey.CortexURLGoogleReview }],
    customerFacing: true,
    variableKey: "Location:CortexLinkReview"
  },
  pay_online: {
    name: "pay_online",
    label: "Pay Online",
    // ENG-3545: the tenant's own spot page first (needs a user in context and both switches on),
    // then the operator's portal URL.
    sources: [
      { kind: "computed", id: "tenantPage" },
      { kind: "gatedVar", enabledKey: LocationVarKey.CortexURLPayOnlineEnabled, valueKey: LocationVarKey.CortexURLPayOnline }
    ],
    customerFacing: true,
    variableKey: "Location:CortexLinkPayOnline"
  },
  account_login: {
    name: "account_login",
    label: "Account Login",
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLAccountLoginEnabled, valueKey: LocationVarKey.CortexURLAccountLogin }],
    customerFacing: true,
    variableKey: "Location:CortexLinkAccountLogin"
  },
  size_guide: {
    name: "size_guide",
    label: "Size Guide",
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLSizeGuideEnabled, valueKey: LocationVarKey.CortexURLSizeGuide }],
    customerFacing: true,
    variableKey: "Location:CortexLinkSizeGuide"
  },
  careers: {
    name: "careers",
    label: "Careers",
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLCareerEnabled, valueKey: LocationVarKey.CortexURLCareer }],
    customerFacing: true,
    variableKey: "Location:CortexLinkCareers"
  },
  referral: {
    name: "referral",
    label: "Referral Program",
    // Backs the URLs-page catalog entry dev added with `CortexURLReferral` (merge, 2026-09-01) —
    // same gatedVar pattern as every catalog-backed link.
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLReferralEnabled, valueKey: LocationVarKey.CortexURLReferral }],
    customerFacing: true,
    variableKey: "Location:CortexLinkReferral"
  },
  privacy_policy: {
    name: "privacy_policy",
    label: "Privacy Policy",
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLPrivacyPolicyEnabled, valueKey: LocationVarKey.CortexURLPrivacyPolicy }],
    customerFacing: true,
    variableKey: "Location:CortexLinkPrivacyPolicy"
  },
  facility_map: {
    name: "facility_map",
    label: "Facility Map",
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexFacilityMapURLEnabled, valueKey: LocationVarKey.CortexFacilityMapURL }],
    customerFacing: true,
    variableKey: "Location:CortexLinkFacilityMap"
  },
  move_out_form: {
    name: "move_out_form",
    label: "Move-out Form",
    sources: [{ kind: "var", key: LocationVarKey.CortexMoveOutFormURL }],
    customerFacing: true,
    variableKey: "Location:CortexLinkMoveOutForm"
  },
  website: {
    name: "website",
    label: "Facility Website",
    sources: [{ kind: "website" }, { kind: "var", key: LocationVarKey.CortexFacilityWebsiteURL }],
    customerFacing: true,
    variableKey: "Location:CortexLinkWebsite"
  },
  spot_home: {
    name: "spot_home",
    label: "spot Page",
    sources: [{ kind: "spotPage", page: "" }],
    customerFacing: true,
    variableKey: "Location:CortexLinkSpotPage"
  },
  spot_review: {
    name: "spot_review",
    label: "spot Review Page",
    sources: [{ kind: "spotPage", page: "review" }],
    customerFacing: true,
    variableKey: "Location:CortexLinkSpotReview"
  },
  spot_referral: {
    name: "spot_referral",
    label: "spot Referral Page",
    // Referrals are out of Conversation Starters (they point at a spot.is link), but the bot should
    // still be able to answer "how do I refer a friend?" — Chuck, 2026-08-27.
    sources: [
      { kind: "var", key: LocationVarKey.CortexSpotReferralURL },
      { kind: "spotPage", page: "referral" }
    ],
    customerFacing: true,
    variableKey: "Location:CortexLinkSpotReferral"
  },
  spot_qr: {
    name: "spot_qr",
    label: "spot QR Page",
    sources: [{ kind: "spotPage", page: "qr" }],
    customerFacing: false,
    variableKey: "Location:CortexLinkSpotQR"
  },
  rent_unit: {
    name: "rent_unit",
    label: "Rent or Reserve Online",
    // ENG-3138: the website provider's rental-form redirect (Storagely, StoragePug), else the
    // operator's facility website — today's behavior, preserved.
    sources: [
      { kind: "computed", id: "websiteProviderRental" },
      { kind: "var", key: LocationVarKey.CortexFacilityWebsiteURL }
    ],
    customerFacing: true,
    variableKey: "Location:CortexLinkRentUnit"
  },
  packing_supplies: {
    name: "packing_supplies",
    label: "Packing Supplies",
    sources: [
      {
        kind: "gatedVar",
        enabledKey: LocationVarKey.CortexURLPackingSuppliesEnabled,
        valueKey: LocationVarKey.CortexURLPackingSupplies,
        featureKey: LocationVarKey.CortexFeaturePackingSupplies
      }
    ],
    customerFacing: true,
    variableKey: "Location:CortexLinkPackingSupplies"
  },
  moving_services: {
    name: "moving_services",
    label: "Moving Services",
    sources: [
      {
        kind: "gatedVar",
        enabledKey: LocationVarKey.CortexURLMovingServicesEnabled,
        valueKey: LocationVarKey.CortexURLMovingServices,
        featureKey: LocationVarKey.CortexFeatureMovingServices
      }
    ],
    customerFacing: true,
    variableKey: "Location:CortexLinkMovingServices"
  },
  insurance: {
    name: "insurance",
    label: "Insurance",
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLInsuranceEnabled, valueKey: LocationVarKey.CortexURLInsurance }],
    customerFacing: true,
    variableKey: "Location:CortexLinkInsurance"
  },
  auctions: {
    name: "auctions",
    label: "Auctions",
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLAuctionsEnabled, valueKey: LocationVarKey.CortexURLAuctions }],
    customerFacing: true,
    variableKey: "Location:CortexLinkAuctions"
  },
  mobile_app: {
    name: "mobile_app",
    label: "Mobile App",
    sources: [{ kind: "gatedVar", enabledKey: LocationVarKey.CortexURLMobileAppEnabled, valueKey: LocationVarKey.CortexURLMobileApp }],
    customerFacing: true,
    variableKey: "Location:CortexLinkMobileApp"
  }
};

export const LOCATION_URL_ENTRIES: LocationUrlEntry[] = Object.values(LOCATION_URL_REGISTRY);

/** The registry entry whose brace-variable key matches, if any (`Location:SpotReviewURL` → spot_review). */
export function locationUrlEntryForVariable(variableKey: string): LocationUrlEntry | undefined {
  return LOCATION_URL_ENTRIES.find((entry) => entry.variableKey === variableKey);
}
